/* eslint-disable camelcase */
import React, { Component } from 'react';
import { StyleSheet, css } from 'aphrodite';
import Tooltip from '@material-ui/core/Tooltip';
import { AppTheme } from '../../../styles/AppTheme';
import { Helper } from '../../../common/helpers/Helper';
import { UiHelper } from '../../../common/helpers/UiHelper';
import { ApiHelper } from '../../../common/helpers/ApiHelper';
import { AppConfig } from '../../../AppConfig';
import { AppButton } from '../../../common/components/AppButton';
import { DynamicFormDialog } from '../../../common/components/DynamicFormDialog';
import { CustomDynamicFormStyles } from '../../../styles/CustomStyles';
import { DocumentUploader } from '../../../common/components/DocumentUploader';
import { LINKEDIN_URLS_MAX, linkedinUrlsFromText, uploadLinkedinUrlsCsv } from './contactsFinderLinkedin';

const MODE_FILTERS = 'filters'
const MODE_COMPANIES = 'companies'
const MODE_LINKEDIN = 'linkedin'
const FIELD_DEF_FILTERS = 'contactFinderFieldDef'
const FIELD_DEF_COMPANIES = 'contactFinderByCompanyFieldDef'
const CONTACT_ACTIONS = [
  {
    label: 'Ignore',
    api: 'contactfinder-ignore',
    appliesToIgnored: false,
    confirmMessage: 'Would you like to ignore these contacts? Once ignored, they will not appear in future searches.',
  },
  {
    label: 'Restore',
    api: 'contactfinder-restore',
    appliesToIgnored: true,
    confirmMessage: 'Would you like to restore these contacts? Once restored, they will start appearing in future searches.',
  },
]

export function startContactFinderImport({ orgId, items }) {
  const contacts = (items || []).map((item) => ({
    personId: item.personId,
    givenname: item.givenname,
    familyname: item.familyname,
    jobTitle: item.jobTitle,
    linkedinProfileUrl: item.linkedinProfileUrl,
    company: item.company,
    companyDomain: item.companyDomain,
    companyWebsiteUrl: item.companyWebsiteUrl,
    companyIndustry: item.companyIndustry,
    companyLinkedinUrl: item.companyLinkedinUrl,
    employeeCount: item.employeeCount,
    location: item.location,
  }))
  return ApiHelper.callAwait({
    method: 'POST',
    ms: AppConfig.CDP_MS.ENDPOINT,
    paths: ['miscs', 'bulk-enrich-import-contact'],
    queryParams: { orgId },
    jsonBody: { orgId, contacts },
  })
}

export class ContactsFinder extends Component {
  constructor(props) {
    super(props)
    this.state = {
      mode: MODE_FILTERS,
      valuesByMode: { [MODE_FILTERS]: {}, [MODE_COMPANIES]: {} },
      contactFinderFieldDef: null,
      contactFinderByCompanyFieldDef: null,
      fieldsLoading: true,
      fieldsError: '',
      filtersOpen: true,
      items: [],
      selectedIds: {},
      activeFieldKey: null,
      optionListOpen: false,
      fieldQuery: '',
      suggestions: [],
      suggestionsQuery: '',
      suggestionsLoading: false,
      menuMaxHeight: 180,
      searching: false,
      searched: false,
      searchError: '',
      searchTotal: null,
      importError: '',
      refreshing: false,
      showIgnored: false,
      hasIgnoredContacts: false,
      contactAction: null,
      contactActionItems: [],
      linkedinCsvKey: '',
      linkedinCsvLink: '',
      linkedinText: '',
      linkedinUploading: false,
    }
    this.suggestionRequestId = 0
    this.debouncedLoadSuggestions = Helper.debounce(this.loadSuggestions, 500)
    this.blurTimer = null
    this.cursorPopper = React.createRef()
  }

  componentDidMount() {
    this.loadFieldDefs()
  }

  componentWillUnmount() {
    this.unmounted = true
    if (this.debouncedLoadSuggestions.cancel) this.debouncedLoadSuggestions.cancel()
    if (this.blurTimer) clearTimeout(this.blurTimer)
  }

  loadFieldDefs = async () => {
    try {
      const result = await ApiHelper.callAwait({
        method: 'GET',
        ms: AppConfig.CDP_MS.ENDPOINT,
        paths: ['miscs', 'contact-search-fields'],
        queryParams: { orgId: this.props.orgId },
      })
      if (this.unmounted) return
      const contactFinderFieldDef = result && result.contactFinderFieldDef
      const contactFinderByCompanyFieldDef = result && result.contactFinderByCompanyFieldDef
      const hasFilters = this.defHasFields(contactFinderFieldDef)
      const hasCompanies = this.defHasFields(contactFinderByCompanyFieldDef)
      const selectedFieldDef = result && result.selectedFieldDef
      let mode = hasFilters || !hasCompanies ? MODE_FILTERS : MODE_COMPANIES
      if (selectedFieldDef === FIELD_DEF_FILTERS && hasFilters) mode = MODE_FILTERS
      else if (selectedFieldDef === FIELD_DEF_COMPANIES && hasCompanies) mode = MODE_COMPANIES
      this.setState({
        fieldsLoading: false,
        contactFinderFieldDef,
        contactFinderByCompanyFieldDef,
        hasIgnoredContacts: !!(result && result.hasIgnoredContacts),
        mode,
        valuesByMode: {
          [MODE_FILTERS]: this.valuesFromFieldDef(contactFinderFieldDef),
          [MODE_COMPANIES]: this.valuesFromFieldDef(contactFinderByCompanyFieldDef),
        },
      })
    } catch (err) {
      if (this.unmounted) return
      this.setState({ fieldsLoading: false, fieldsError: Helper.getErrorMsg(err) })
    }
  }

  valuesFromFieldDef(fieldDef) {
    const fields = fieldDef && fieldDef.steps && fieldDef.steps[0] && fieldDef.steps[0].fields
    if (!Array.isArray(fields)) return {}
    const modeValues = {}
    fields.forEach((field) => {
      if (!field || !field.key || !Array.isArray(field.defaultValue) || field.defaultValue.length === 0) return
      const options = Array.isArray(field.options) ? field.options : []
      const values = []
      field.defaultValue.forEach((item) => {
        if (item == null || item === '') return
        const key = (item && typeof item === 'object') ? item.key : item
        if (key == null || key === '') return
        if (values.some((value) => String(value.key) === String(key))) return
        const option = options.find((entry) => entry && String(entry.key) === String(key))
        values.push({
          key: option ? option.key : key,
          label: option ? (option.label || option.key) : String(key),
        })
      })
      if (values.length) modeValues[field.key] = values
    })
    return modeValues
  }

  componentDidUpdate() {
    const modes = this.availableModes()
    if (modes.length && modes.indexOf(this.state.mode) === -1) this.setMode(modes[0])
  }

  defHasFields(fieldDef) {
    const fields = fieldDef && fieldDef.steps && fieldDef.steps[0] && fieldDef.steps[0].fields
    return Array.isArray(fields) && fields.length > 0
  }

  availableModes() {
    const modes = []
    if (this.defHasFields(this.state.contactFinderFieldDef)) modes.push(MODE_FILTERS)
    if (this.defHasFields(this.state.contactFinderByCompanyFieldDef)) modes.push(MODE_COMPANIES)
    modes.push(MODE_LINKEDIN)
    return modes
  }

  getFieldDef(mode = this.state.mode) {
    if (mode === MODE_COMPANIES) return this.state.contactFinderByCompanyFieldDef
    return this.state.contactFinderFieldDef
  }

  getFields(mode = this.state.mode) {
    const fieldDef = this.getFieldDef(mode)
    const fields = fieldDef && fieldDef.steps && fieldDef.steps[0] && fieldDef.steps[0].fields
    return Array.isArray(fields) ? fields : []
  }

  getModeValues(mode = this.state.mode) {
    return (this.state.valuesByMode && this.state.valuesByMode[mode]) || {}
  }

  getFieldValues(fieldKey, mode = this.state.mode) {
    const values = this.getModeValues(mode)[fieldKey]
    return Array.isArray(values) ? values : []
  }

  setMode = (mode) => {
    this.suggestionRequestId += 1
    this.setState({
      mode,
      activeFieldKey: null,
      optionListOpen: false,
      fieldQuery: '',
      suggestions: [],
      suggestionsQuery: '',
      suggestionsLoading: false,
      searchError: '',
      importError: '',
    })
  }

  setFieldValues(fieldKey, nextValues) {
    const mode = this.state.mode
    const modeValues = { ...this.getModeValues(mode), [fieldKey]: nextValues }
    this.setState({
      valuesByMode: { ...this.state.valuesByMode, [mode]: modeValues },
      searchError: '',
      importError: '',
    })
  }

  isDropdownField(field) {
    return !!(field && (field.type === 'radio' || field.type === 'check_box'))
  }

  hasAnyValue() {
    const values = this.getModeValues()
    return Object.keys(values).some((key) => Array.isArray(values[key]) && values[key].length > 0)
  }

  hasSuggestOptions(field) {
    return !!(field && field.suggestOptions && field.suggestOptions.api)
  }

  loadSuggestions = async (field, query) => {
    const requestId = ++this.suggestionRequestId
    this.setState({ suggestionsLoading: true })
    const suggest = field.suggestOptions
    const paths = String(suggest.api || '').split('/').filter(Boolean)
    try {
      const result = await ApiHelper.callAwait({
        method: 'POST',
        ms: AppConfig.CDP_MS.ENDPOINT,
        paths,
        queryParams: { orgId: this.props.orgId },
        jsonBody: { orgId: this.props.orgId, key: suggest.key, query },
      })
      if (requestId !== this.suggestionRequestId) return
      if (this.state.activeFieldKey !== field.key || this.state.fieldQuery.trim() !== query) return
      this.setState({ suggestions: (result && result.options) || [], suggestionsQuery: query, suggestionsLoading: false })
    } catch (err) {
      if (requestId !== this.suggestionRequestId) return
      if (this.state.activeFieldKey !== field.key || this.state.fieldQuery.trim() !== query) return
      this.setState({ suggestions: [], suggestionsQuery: query, suggestionsLoading: false, searchError: Helper.getErrorMsg(err) })
    }
  }

  onQueryChange = (field, query) => {
    this.setState({ activeFieldKey: field.key, fieldQuery: query, optionListOpen: true })
    if (!this.hasSuggestOptions(field)) return
    const minChars = field.suggestOptions.minChars
    if (!(query.trim().length >= minChars)) {
      if (this.debouncedLoadSuggestions.cancel) this.debouncedLoadSuggestions.cancel()
      this.suggestionRequestId += 1
      this.setState({ suggestions: [], suggestionsQuery: '', suggestionsLoading: false })
      return
    }
    this.debouncedLoadSuggestions(field, query.trim())
  }

  onBoxMouseDown(field, event) {
    const tag = event.target && event.target.tagName
    if (tag === 'BUTTON' || tag === 'INPUT' || (event.target.closest && event.target.closest('button'))) return
    event.preventDefault()
    const input = document.getElementById(`finder-${field.key}`)
    if (input) input.focus()
  }

  onFieldFocus = (field) => {
    if (this.blurTimer) clearTimeout(this.blurTimer)
    if (this.state.activeFieldKey !== field.key) {
      this.suggestionRequestId += 1
      this.setState({ activeFieldKey: field.key, fieldQuery: '', suggestions: [], suggestionsQuery: '', suggestionsLoading: false, optionListOpen: true })
    } else if (!this.state.optionListOpen) {
      this.setState({ optionListOpen: true })
    }
  }

  onFieldBlur = () => {
    this.blurTimer = setTimeout(() => {
      this.suggestionRequestId += 1
      this.setState({ activeFieldKey: null, optionListOpen: false, fieldQuery: '', suggestions: [], suggestionsQuery: '', suggestionsLoading: false })
    }, 150)
  }

  onArrowMouseDown = (event) => {
    event.preventDefault()
    event.stopPropagation()
  }

  isOptionListVisible(field) {
    return !!(field && this.state.activeFieldKey === field.key && this.state.optionListOpen)
  }

  onArrowClick = (field, event) => {
    event.preventDefault()
    event.stopPropagation()
    if (this.isOptionListVisible(field)) {
      this.setState({ optionListOpen: false })
      return
    }
    this.setState({ activeFieldKey: field.key, optionListOpen: true })
    const input = document.getElementById(`finder-${field.key}`)
    if (input) input.focus()
  }

  selectOption = (field, option) => {
    const current = this.getFieldValues(field.key)
    const existing = current.find((value) => String(value.key).toLowerCase() === String(option.key).toLowerCase())
    if (option.type !== 'add' && existing) {
      this.removeValue(field, existing.key)
      return
    }
    if (existing) return
    const nextValue = { key: option.key, label: option.label || option.key }
    this.setFieldValues(field.key, current.concat(nextValue))
    if (option.type !== 'add') return
    this.suggestionRequestId += 1
    this.setState({
      fieldQuery: '',
      suggestions: [],
      suggestionsQuery: '',
      suggestionsLoading: false,
      optionListOpen: true,
    })
  }

  removeValue = (field, optionKey) => {
    this.setFieldValues(field.key, this.getFieldValues(field.key).filter((value) => value.key !== optionKey))
  }

  getMenuItems(field) {
    if (this.state.activeFieldKey !== field.key) return []
    const query = this.state.fieldQuery.trim()
    const selected = new Set(this.getFieldValues(field.key).map((value) => String(value.key).toLowerCase()))
    let options = []
    if (this.hasSuggestOptions(field)) {
      if (!(query.length >= field.suggestOptions.minChars)) return []
      if (this.state.suggestionsLoading || this.state.suggestionsQuery !== query) return []
      options = this.state.suggestions || []
    } else if (Array.isArray(field.options)) {
      const q = query.toLowerCase()
      options = field.options.filter((option) => {
        if (!q) return true
        return String(option.label).toLowerCase().includes(q) || String(option.key).toLowerCase().includes(q)
      })
    }
    const items = options
      .map((option) => ({ type: 'option', key: option.key, label: option.label || option.text || option.key }))
    if (field.addOptions && query) {
      const formattedValue = Helper.formatAddOptionInput(query, field.addOptionsFormatter) || query
      const normalized = String(formattedValue).toLowerCase()
      const exists = selected.has(normalized)
        || items.some((item) => String(item.key).toLowerCase() === normalized || String(item.label).toLowerCase() === normalized)
        || (Array.isArray(field.options) && field.options.some((option) => {
          const optLabel = option.text || option.label || option.key
          return query === optLabel || formattedValue === optLabel || formattedValue === option.key
        }))
      if (formattedValue && !exists) {
        items.push({ type: 'add', key: formattedValue, label: formattedValue, text: `Add "${formattedValue}"` })
      }
    }
    return items
  }

  getMenuHint(field, items) {
    if (!field || this.state.activeFieldKey !== field.key) return ''
    const query = this.state.fieldQuery.trim()
    if (this.hasSuggestOptions(field)) {
      if (!(query.length >= field.suggestOptions.minChars)) return Helper.getString('optionListLoadingText')
      if (this.state.suggestionsLoading || this.state.suggestionsQuery !== query) return 'Loading...'
      return ''
    }
    if (!items.length) return field.addOptions ? Helper.getString('optionListLoadingText') : 'No options'
    return ''
  }

  updateMenuMaxHeight = () => {
    if (!this.menuNode || !this.footerNode) return
    const available = Math.floor(this.footerNode.getBoundingClientRect().top - this.menuNode.getBoundingClientRect().top)
    const menuMaxHeight = Math.max(180, available)
    if (this.state.menuMaxHeight !== menuMaxHeight) this.setState({ menuMaxHeight })
  }

  onInputKeyDown = (field, event) => {
    if (event.key === 'Backspace' && !this.state.fieldQuery) {
      const current = this.getFieldValues(field.key)
      if (current.length) this.removeValue(field, current[current.length - 1].key)
      return
    }
    if (event.key !== 'Enter') return
    event.preventDefault()
    const query = this.state.fieldQuery.trim()
    const items = this.getMenuItems(field)
    const addItem = items.find((item) => item.type === 'add')
    if (field.addOptions && query && addItem) {
      this.selectOption(field, addItem)
      return
    }
    if (items[0]) this.selectOption(field, items[0])
  }

  searchValues() {
    const raw = this.getModeValues()
    const values = {}
    Object.keys(raw).forEach((key) => {
      values[key] = (raw[key] || []).map((value) => value.key)
    })
    return values
  }

  runSearch = async (refresh = false) => {
    if (this.state.searching) return
    if (!this.hasAnyValue()) {
      this.setState({ searchError: 'Select at least one filter', importError: '' })
      return
    }
    this.setState({ searching: true, refreshing: refresh, optionListOpen: false, searchError: '', importError: '' })
    try {
      const jsonBody = {
        orgId: this.props.orgId,
        selectedFieldDef: this.state.mode === MODE_COMPANIES ? FIELD_DEF_COMPANIES : FIELD_DEF_FILTERS,
        showIgnored: this.state.showIgnored,
      }
      const values = this.searchValues()
      Object.keys(values).forEach((key) => {
        if (Array.isArray(values[key]) && values[key].length) jsonBody[key] = values[key]
      })
      const result = await ApiHelper.callAwait({
        method: 'POST',
        ms: AppConfig.CDP_MS.ENDPOINT,
        paths: ['miscs', 'search-contacts'],
        queryParams: { orgId: this.props.orgId },
        jsonBody,
      })
      const items = ((result && result.items) || []).slice(0, 25)
      const totalCount = result && result.pagination ? result.pagination.total_count : undefined
      const searchTotal = (totalCount === 0 || totalCount) && Number.isFinite(Number(totalCount)) ? Number(totalCount) : null
      const otherMode = this.state.mode === MODE_COMPANIES ? MODE_FILTERS : MODE_COMPANIES
      const hasIgnoredContacts = !!(result && result.hasIgnoredContacts)
      this.setState({
        searching: false,
        refreshing: false,
        searchError: '',
        searchTotal,
        importError: '',
        hasIgnoredContacts,
        showIgnored: hasIgnoredContacts && this.state.showIgnored,
        items,
        selectedIds: {},
        filtersOpen: false,
        searched: true,
        activeFieldKey: null,
        optionListOpen: false,
        fieldQuery: '',
        suggestions: [],
        suggestionsQuery: '',
        suggestionsLoading: false,
        valuesByMode: { ...this.state.valuesByMode, [otherMode]: {} },
      })
    } catch (err) {
      this.setState({ searching: false, refreshing: false, searchError: Helper.getErrorMsg(err), importError: '' })
    }
  }

  editFilters = () => {
    this.setState({ filtersOpen: true })
  }

  allSelected() {
    return this.state.items.length > 0 && this.state.items.every((item) => this.state.selectedIds[this.rowId(item)])
  }

  selectionChanged(nextSelectedIds) {
    const current = this.state.selectedIds || {}
    const currentKeys = Object.keys(current)
    const nextKeys = Object.keys(nextSelectedIds || {})
    if (currentKeys.length !== nextKeys.length) return true
    return nextKeys.some((key) => !current[key])
  }

  toggleAll = () => {
    const selectedIds = {}
    if (!this.allSelected()) {
      this.state.items.forEach((item) => { selectedIds[this.rowId(item)] = true })
    }
    const next = { selectedIds }
    if (this.selectionChanged(selectedIds)) next.importError = ''
    this.setState(next)
  }

  toggleItem = (item) => {
    const id = this.rowId(item)
    const selectedIds = { ...this.state.selectedIds }
    if (selectedIds[id]) delete selectedIds[id]
    else selectedIds[id] = true
    const next = { selectedIds }
    if (this.selectionChanged(selectedIds)) next.importError = ''
    this.setState(next)
  }

  rowId(item) {
    return item.personId || item.id
  }

  formatIndustry(industry) {
    if (Array.isArray(industry)) {
      if (industry.length <= 1) return industry[0] || ''
      return industry.join(', ')
    }
    return industry || ''
  }

  formatLocation(location) {
    if (!location) return ''
    if (typeof location === 'string') return location
    if (location.raw_address) return location.raw_address
    return [location.city, location.state, location.country].filter(Boolean).join(', ')
  }

  renderSearchTotal() {
    if (this.state.searchError || this.state.searchTotal == null) return null
    return <span className={css(Styles.searchTotal)}>Total search results (with valid emails): {Helper.abbrNum(this.state.searchTotal)}</span>
  }

  renderFilters() {
    const modes = this.availableModes()
    const mode = modes.indexOf(this.state.mode) === -1 ? modes[0] : this.state.mode
    const fields = this.getFields(mode)
    const gridStyle = mode === MODE_COMPANIES ? Styles.fieldGridCompanies : Styles.fieldGrid
    return (
      <div className={css(Styles.filters)}>
        <div className={css(Styles.modeRow)} role='radiogroup' aria-label='Search by'>
          <span className={css(Styles.modeLabel)}>Search by</span>
          {modes.indexOf(MODE_FILTERS) !== -1 && (
            <label className={css(Styles.modeOption)}>
              <input
                className={css(Styles.radio)}
                type='radio'
                name='contact-finder-mode'
                checked={mode === MODE_FILTERS}
                onChange={() => this.setMode(MODE_FILTERS)}
              />
              Filters
            </label>
          )}
          {modes.indexOf(MODE_COMPANIES) !== -1 && (
            <label className={css(Styles.modeOption)}>
              <input
                className={css(Styles.radio)}
                type='radio'
                name='contact-finder-mode'
                checked={mode === MODE_COMPANIES}
                onChange={() => this.setMode(MODE_COMPANIES)}
              />
              Specific companies
            </label>
          )}
          {modes.indexOf(MODE_LINKEDIN) !== -1 && (
            <label className={css(Styles.modeOption)}>
              <input
                className={css(Styles.radio)}
                type='radio'
                name='contact-finder-mode'
                checked={mode === MODE_LINKEDIN}
                onChange={() => this.setMode(MODE_LINKEDIN)}
              />
              LinkedIn
            </label>
          )}
        </div>
        {mode !== MODE_LINKEDIN && (
          <div className={css(Styles.fieldControls)}>
            <div className={css(gridStyle)}>
              {fields.map((field) => this.renderField(field))}
            </div>
            <div className={css(Styles.searchField)}>
              <div className={css(Styles.searchRow)}>
                {this.state.searchError && <span className={css(Styles.actionError)} dangerouslySetInnerHTML={{ __html: Helper.renderHtmlOrPlainText(this.state.searchError) }} />}
                {this.renderSearchTotal()}
                <AppButton
                  buttonText='Search'
                  icon='search'
                  buttonStyle={Styles.searchButton}
                  buttonTextStyle={Styles.searchButtonText}
                  iconContainerStyle={Styles.plainIcon}
                  inProcess={this.state.searching && !this.state.refreshing}
                  onClick={() => this.runSearch()}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  renderLinkedinInput() {
    const text = this.state.linkedinText
    const csvKey = this.state.linkedinCsvKey
    const csvDisabled = !!text.trim()
    return (
      <div className={css(Styles.linkedinInput)}>
        <div className={css(Styles.linkedinLabelRow)}>
          <label className={css(Styles.fieldLabel)} htmlFor='finder-linkedin-urls'>LinkedIn profile URLs (Maximum {LINKEDIN_URLS_MAX} URLs)</label>
          <Tooltip title={`Type or paste up to ${LINKEDIN_URLS_MAX} LinkedIn profile URLs`} classes={{ popper: css(Styles.tooltip) }}>
            <i className={`material-icons-outlined ${css(Styles.csvInfoIcon)}`}>info</i>
          </Tooltip>
        </div>
        <Tooltip title={csvKey ? 'Remove the uploaded CSV to enter URLs instead' : ''} placement='top' classes={{ popper: css(Styles.tooltip) }} PopperProps={this.cursorPopperProps()}>
          <div className={css(Styles.urlField)} onMouseMove={this.trackCursor}>
            <textarea
              id='finder-linkedin-urls'
              className={css(Styles.urlTextarea)}
              value={text}
              disabled={!!csvKey}
              onChange={(event) => this.setState({ linkedinText: event.target.value, importError: '' })}
            />
          </div>
        </Tooltip>
        <div className={css(Styles.orDivider)}>
          <span className={css(Styles.orLine)} />
          OR
          <span className={css(Styles.orLine)} />
        </div>
        <div className={css(Styles.linkedinLabelRow)}>
          <span className={css(Styles.fieldLabel)}>Import CSV</span>
          <Tooltip title={'Select a csv file with a \'linkedinurl\' column, one LinkedIn profile URL per row'} classes={{ popper: css(Styles.tooltip) }}>
            <i className={`material-icons-outlined ${css(Styles.csvInfoIcon)}`}>info</i>
          </Tooltip>
          <a href={AppConfig.BULK_PROSPECT_SAMPLE_FILE_URL} target='_blank' rel='noopener noreferrer' className={css(CustomDynamicFormStyles.link)}>Sample File</a>
        </div>
        <Tooltip title={csvDisabled ? 'Clear the LinkedIn URLs above to upload a CSV instead' : ''} placement='top' classes={{ popper: css(Styles.tooltip) }} PopperProps={this.cursorPopperProps()}>
          <div
            className={css(Styles.csvUploader)}
            onMouseMove={this.trackCursor}
          >
            <div className={css(csvDisabled && Styles.csvUploaderDisabled)}>
              <DocumentUploader
                uploadId='linkedinCsv'
                link={this.state.linkedinCsvLink}
                value={csvKey}
                uploadAccept='text/csv'
                uploadLocation={this.props.orgId}
                uploadMs={AppConfig.CDP_MS.ENDPOINT}
                disabled={csvDisabled}
                onUploadSuccess={this.onLinkedinCsvUpload}
              />
            </div>
          </div>
        </Tooltip>
      </div>
    )
  }

  trackCursor = (event) => {
    this.cursorPosition = { x: event.clientX, y: event.clientY }
    if (this.cursorPopper.current) this.cursorPopper.current.update()
  }

  cursorPopperProps() {
    return {
      popperRef: this.cursorPopper,
      anchorEl: {
        clientWidth: 0,
        clientHeight: 0,
        getBoundingClientRect: () => {
          const { x, y } = this.cursorPosition || { x: 0, y: 0 }
          return { top: y, bottom: y, left: x, right: x, width: 0, height: 0 }
        },
      },
    }
  }

  onLinkedinCsvUpload = (uploadId, signedUploadInfo) => {
    this.setState({ linkedinCsvKey: signedUploadInfo.fields.key, linkedinCsvLink: signedUploadInfo.link, importError: '' })
  }

  linkedinImportReady() {
    return !!this.state.linkedinCsvKey || linkedinUrlsFromText(this.state.linkedinText).length > 0
  }

  onLinkedinImport = async () => {
    if (this.state.linkedinCsvKey) {
      this.props.onLinkedinImport(this.state.linkedinCsvKey)
      return
    }
    const urls = linkedinUrlsFromText(this.state.linkedinText)
    if (urls.length > LINKEDIN_URLS_MAX) {
      this.setState({ importError: `Only up to ${LINKEDIN_URLS_MAX} LinkedIn URLs are allowed at a time` })
      return
    }
    this.setState({ linkedinUploading: true, importError: '' })
    try {
      const importCsv = await uploadLinkedinUrlsCsv({ orgId: this.props.orgId, urls })
      if (this.unmounted) return
      this.setState({ linkedinUploading: false })
      this.props.onLinkedinImport(importCsv, urls.length)
    } catch (err) {
      this.setState({ linkedinUploading: false, importError: Helper.getErrorMsg(err) })
    }
  }

  renderField(field) {
    const values = this.getFieldValues(field.key)
    const menuItems = this.getMenuItems(field)
    const menuHint = this.getMenuHint(field, menuItems)
    const listOpen = this.isOptionListVisible(field)
    const showMenu = listOpen
    const showCaret = this.isDropdownField(field)
    const showClear = values.length > 0
    return (
      <div className={css(Styles.field)} key={field.key}>
        <label className={css(Styles.fieldLabel)} htmlFor={`finder-${field.key}`}>{field.label}</label>
        <div
          className={css(Styles.chipBox, showCaret && Styles.chipBoxWithCaret, showClear && Styles.chipBoxWithClear, showClear && showCaret && Styles.chipBoxWithClearAndCaret)}
          onMouseDown={(event) => this.onBoxMouseDown(field, event)}
        >
          {values.map((value) => (
            <span className={css(Styles.chip)} key={value.key}>
              <span className={css(Styles.chipLabel)}>{value.label}</span>
              <button
                type='button'
                className={css(Styles.chipRemove)}
                aria-label={`Remove ${value.label}`}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => this.removeValue(field, value.key)}
              >
                <i className={`material-icons-outlined ${css(Styles.chipRemoveIcon)}`}>close</i>
              </button>
            </span>
          ))}
          <input
            id={`finder-${field.key}`}
            className={css(Styles.chipInput)}
            value={this.state.activeFieldKey === field.key ? this.state.fieldQuery : ''}
            placeholder=''
            onFocus={() => this.onFieldFocus(field)}
            onBlur={this.onFieldBlur}
            onChange={(event) => this.onQueryChange(field, event.target.value)}
            onKeyDown={(event) => this.onInputKeyDown(field, event)}
          />
          {showClear && (
            <button
              type='button'
              className={css(Styles.fieldClear, showCaret && Styles.fieldClearWithCaret)}
              aria-label={`Clear ${field.label}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => this.setFieldValues(field.key, [])}
            >
              <i className={`material-icons-outlined ${css(Styles.fieldClearIcon)}`}>close</i>
            </button>
          )}
          {showCaret && (
            <button
              type='button'
              className={css(Styles.caret)}
              aria-label={listOpen ? 'Close options' : 'Open options'}
              onMouseDown={this.onArrowMouseDown}
              onClick={(event) => this.onArrowClick(field, event)}
            >
              <i className={`material-icons-outlined ${css(Styles.caretIcon)}`}>{listOpen ? 'expand_less' : 'expand_more'}</i>
            </button>
          )}
          {showMenu && (
            <div
              className={css(Styles.menu)}
              style={{ maxHeight: this.state.menuMaxHeight }}
              ref={(node) => {
                this.menuNode = node
                if (node) this.updateMenuMaxHeight()
              }}
              onMouseDown={(event) => event.preventDefault()}
            >
              {menuHint && <div className={css(Styles.menuEmpty)}>{menuHint}</div>}
              {menuItems.map((item) => {
                const checked = values.some((value) => String(value.key).toLowerCase() === String(item.key).toLowerCase())
                return (
                  <button
                    type='button'
                    key={`${item.type}-${item.key}`}
                    className={css(Styles.menuItem)}
                    onClick={() => this.selectOption(field, item)}
                  >
                    {item.type === 'option' && (
                      <input
                        type='checkbox'
                        className={css(Styles.checkbox, Styles.menuCheckbox)}
                        checked={checked}
                        readOnly
                        tabIndex={-1}
                      />
                    )}
                    <span>{item.text || item.label}</span>
                  </button>
                )
              })}
              {!menuHint && menuItems.length === 0 && <div className={css(Styles.menuEmpty)}>No matches</div>}
            </div>
          )}
        </div>
      </div>
    )
  }

  renderSummary() {
    const chips = this.getFields().map((field) => {
      const values = this.getFieldValues(field.key)
      if (!values.length) return null
      return (
        <span className={css(Styles.summaryChip)} key={field.key}>
          <span className={css(Styles.summaryChipLabel)}>{field.label}:</span>
          <span className={css(Styles.summaryChipValue)}>{values.map((value) => value.label).join(', ')}</span>
        </span>
      )
    }).filter(Boolean)
    return (
      <div className={css(Styles.summary)}>
        <div className={css(Styles.summaryChips)}>
          {chips}
        </div>
        <div className={css(Styles.summaryActions)}>
          {this.state.searchError && <span className={css(Styles.actionError)} dangerouslySetInnerHTML={{ __html: Helper.renderHtmlOrPlainText(this.state.searchError) }} />}
          {this.renderSearchTotal()}
          <button type='button' className={css(Styles.editButton)} onClick={this.editFilters}>
            <i className={`material-icons-outlined ${css(Styles.editIcon)}`}>edit</i>
            Edit filters
          </button>
        </div>
      </div>
    )
  }

  renderTable() {
    const items = this.state.items
    return (
      <div className={css(Styles.table)}>
        <div className={css(Styles.tableHeader)}>
          <div className={css(Styles.checkCell)}>
            <input
              type='checkbox'
              className={css(Styles.checkbox)}
              aria-label='Select all results'
              checked={this.allSelected()}
              onChange={this.toggleAll}
            />
          </div>
          <div>Person</div>
          <div>Company & size</div>
          <div>Location & keywords</div>
        </div>
        <div className={css(Styles.tableBody)}>
          {this.state.refreshing && UiHelper.inPlaceContentLoadingView()}
          {!this.state.refreshing && items.length === 0 && (
            <div className={css(Styles.empty)}>
              <p className={css(Styles.emptyText)}>{this.state.searched ? 'No results found.' : 'Add at least one filter, like job title or location, then click Search.'}</p>
            </div>
          )}
          {!this.state.refreshing && items.map((item, index) => this.renderRow(item, index))}
        </div>
      </div>
    )
  }

  renderRow(item, index) {
    const id = this.rowId(item)
    const name = item.name || item.full_name
    const jobTitle = item.jobTitle || item.job_title
    const linkedinUrl = item.linkedinProfileUrl || item.linkedin_url
    const companyName = item.company || item.company_name
    const companyUrl = item.companyWebsiteUrl || item.company_url
    const companyLinkedinUrl = item.companyLinkedinUrl || item.company_linkedin_url
    const employeeCount = item.employeeCount != null ? item.employeeCount : item.company_employees
    const selected = !!this.state.selectedIds[id]
    const ignored = !!item.ignored
    const industry = this.formatIndustry(item.companyIndustry || item.industry)
    const employees = employeeCount != null && employeeCount !== '' ? `${employeeCount} employees` : ''
    const companyMeta = [industry, employees].filter(Boolean).join(' · ')
    const location = this.formatLocation(item.location)
    const keywords = Array.isArray(item.keywords) ? item.keywords.filter(Boolean) : []
    const moreCount = keywords.length > 1 ? keywords.length - 1 : 0
    return (
      <div
        key={id || index}
        className={css(Styles.row, selected && Styles.rowSelected)}
      >
        <div className={css(Styles.checkCell)}>
          <input
            type='checkbox'
            className={css(Styles.checkbox)}
            aria-label={`Select ${name || 'contact'}`}
            checked={selected}
            onChange={() => this.toggleItem(item)}
          />
        </div>
        <div className={css(Styles.cell, ignored && Styles.ignoredCell)}>
          <div className={css(Styles.primaryLine)}>
            <Tooltip title={name || ''} placement='top' classes={{ popper: css(Styles.tooltip) }}>
              <span className={css(Styles.personName)}>{name}</span>
            </Tooltip>
            {linkedinUrl && (
              <a
                className={css(Styles.iconButton, Styles.iconButtonFill)}
                href={linkedinUrl}
                target='_blank'
                rel='noopener noreferrer'
                aria-label={`${name} LinkedIn profile`}
                onClick={(event) => event.stopPropagation()}
              >in</a>
            )}
          </div>
          <Tooltip title={jobTitle || ''} placement='top' classes={{ popper: css(Styles.tooltip) }}>
            <span className={css(Styles.secondaryLine)}>{jobTitle}</span>
          </Tooltip>
        </div>
        <div className={css(Styles.cell, ignored && Styles.ignoredCell)}>
          <div className={css(Styles.primaryLine)}>
            <Tooltip title={companyName || ''} placement='top' classes={{ popper: css(Styles.tooltip) }}>
              <span className={css(Styles.companyName)}>{companyName}</span>
            </Tooltip>
            {companyUrl && (
              <a
                className={css(Styles.iconButton)}
                href={companyUrl}
                target='_blank'
                rel='noopener noreferrer'
                aria-label={`${companyName} website`}
                title={companyUrl}
                onClick={(event) => event.stopPropagation()}
              >
                <i className='material-icons-outlined' style={{ fontSize: 12, lineHeight: 1 }}>public</i>
              </a>
            )}
            {companyLinkedinUrl && (
              <a
                className={css(Styles.iconButton, Styles.iconButtonLinkedIn)}
                href={companyLinkedinUrl}
                target='_blank'
                rel='noopener noreferrer'
                aria-label={`${companyName} LinkedIn page`}
                onClick={(event) => event.stopPropagation()}
              >in</a>
            )}
          </div>
          <Tooltip title={companyMeta || ''} placement='top' classes={{ popper: css(Styles.tooltip) }}>
            <span className={css(Styles.secondaryLine)}>{companyMeta}</span>
          </Tooltip>
        </div>
        <div className={css(Styles.cell, ignored && Styles.ignoredCell)}>
          <span className={css(Styles.locationLine)}>
            <i className={`material-icons-outlined ${css(Styles.metaIcon)}`}>place</i>
            <Tooltip title={location || ''} placement='top' classes={{ popper: css(Styles.tooltip) }}>
              <span className={css(Styles.locationText)}>{location}</span>
            </Tooltip>
          </span>
          {keywords.length > 0 && (
            <span className={css(Styles.keywordLine)}>
              <span className={css(Styles.keyword)}>{keywords[0]}</span>
              {moreCount > 0 && (
                <Tooltip title={keywords.join(', ')} placement='top' classes={{ popper: css(Styles.tooltip) }}>
                  <span className={css(Styles.keywordMore)}>+{moreCount}</span>
                </Tooltip>
              )}
            </span>
          )}
        </div>
      </div>
    )
  }

  onFindEmailImport = () => {
    const items = this.state.items.filter((item) => this.state.selectedIds[this.rowId(item)])
    if (!items.length || !this.props.onFindEmailImport) {
      this.setState({ importError: 'Select at least one contact', searchError: '' })
      return
    }
    this.setState({ importError: '', searchError: '' })
    this.props.onFindEmailImport(items)
  }

  toggleShowIgnored = () => {
    this.setState({ showIgnored: !this.state.showIgnored }, () => {
      if (this.state.searched) this.runSearch(true)
    })
  }

  openContactAction = (contactAction) => {
    const items = this.state.items.filter((item) => this.state.selectedIds[this.rowId(item)])
    if (!items.length) {
      this.setState({ importError: 'Select at least one contact', searchError: '' })
      return
    }
    this.setState({ importError: '', searchError: '', contactAction, contactActionItems: items })
  }

  closeContactAction = () => {
    this.setState({ contactAction: null, contactActionItems: [] })
  }

  submitContactAction = async ({ caller }) => {
    const items = this.state.contactActionItems.map((item) => ({ personId: item.personId, linkedinProfileUrl: item.linkedinProfileUrl }))
    try {
      await ApiHelper.callAwait({
        method: 'POST',
        ms: AppConfig.CDP_MS.ENDPOINT,
        paths: ['miscs', this.state.contactAction.api],
        queryParams: { orgId: this.props.orgId },
        jsonBody: { items },
      })
      if (this.unmounted) return
      this.closeContactAction()
      this.runSearch(true)
    } catch (err) {
      caller.setState({ inProcess: false, success: false, message: Helper.getErrorMsg(err) })
    }
  }

  renderContactActionDialog() {
    const count = this.state.contactActionItems.length
    const contactLabel = count === 1 ? 'Contact' : 'Contacts'
    return (
      <DynamicFormDialog
        formDefinition={{
          title: `${this.state.contactAction.label} (${count} ${contactLabel})`,
          customStyles: { confirmMessageStyles: Styles.confirmMessage },
          confirmMessage: this.state.contactAction.confirmMessage,
          formType: 'add',
          ctaButtonText: 'Continue',
          steps: [{ fields: [] }],
        }}
        maxWidth='sm'
        nestedDialog
        isOpen={!!this.state.contactAction}
        onClosed={this.closeContactAction}
        customSubmit={this.submitContactAction}
      />
    )
  }

  renderFooter() {
    const linkedin = this.state.mode === MODE_LINKEDIN
    const selectedItems = this.state.items.filter((item) => this.state.selectedIds[this.rowId(item)])
    const importDisabled = linkedin ? !this.linkedinImportReady() : !selectedItems.length
    return (
      <div className={css(Styles.footer)} ref={(node) => { this.footerNode = node }}>
        <div className={css(Styles.footerInfo)}>
          {!linkedin && (
            <div className={css(Styles.footerNote)}>
              <i className={`material-icons-outlined ${css(Styles.metaIcon)}`}>info</i>
              <span>You'll see up to 25 contacts not yet in your account. Import them, then search again to see more</span>
            </div>
          )}
          {!linkedin && (
            <div className={css(Styles.contactActions)}>
              {CONTACT_ACTIONS.map((action) => (
                <button
                  type='button'
                  key={action.label}
                  className={css(Styles.secondaryButton)}
                  disabled={this.state.searching || !selectedItems.some((item) => !!item.ignored === action.appliesToIgnored)}
                  onClick={() => this.openContactAction(action)}
                >{action.label}</button>
              ))}
              {this.state.hasIgnoredContacts && (
                <label className={css(Styles.showIgnored)}>
                  <input
                    type='checkbox'
                    className={css(Styles.checkbox)}
                    checked={this.state.showIgnored}
                    disabled={this.state.searching}
                    onChange={this.toggleShowIgnored}
                  />
                  Show Ignored
                </label>
              )}
            </div>
          )}
        </div>
        <div className={css(Styles.footerActions)}>
          {this.state.importError && <span className={css(Styles.actionError)} dangerouslySetInnerHTML={{ __html: Helper.renderHtmlOrPlainText(this.state.importError) }} />}
          <AppButton
            buttonText='Find email & Import'
            icon='email'
            iconContainerStyle={Styles.plainIcon}
            buttonStyle={importDisabled && CustomDynamicFormStyles.buttonDisabled}
            buttonTextStyle={importDisabled && CustomDynamicFormStyles.buttonDisabledText}
            iconStyle={importDisabled && CustomDynamicFormStyles.buttonDisabledText}
            disabled={importDisabled}
            inProcess={this.state.linkedinUploading}
            onClick={linkedin ? this.onLinkedinImport : this.onFindEmailImport}
          />
        </div>
      </div>
    )
  }

  render() {
    if (this.state.fieldsLoading) return UiHelper.componentLoadingView(true)
    if (this.state.fieldsError) {
      return (
        <div className={css(Styles.container)}>
          <span className={css(Styles.fieldsError)} dangerouslySetInnerHTML={{ __html: Helper.renderHtmlOrPlainText(this.state.fieldsError) }} />
        </div>
      )
    }
    const showSearchBlock = this.availableModes().length > 0
    return (
      <div className={css(Styles.container)}>
        {showSearchBlock && (this.state.filtersOpen ? this.renderFilters() : this.renderSummary())}
        {this.state.mode === MODE_LINKEDIN ? this.renderLinkedinInput() : this.renderTable()}
        {this.renderFooter()}
        {this.state.contactAction && this.renderContactActionDialog()}
      </div>
    )
  }
}

const Styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 520,
    marginLeft: -30,
    marginRight: -30,
    marginBottom: -15,
    '@media (max-width: 1024px)': {
      marginLeft: -15,
      marginRight: -15,
    },
  },
  filters: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    padding: '16px 28px 18px',
    backgroundColor: AppTheme.secondaryBg,
    borderBottom: `1px solid ${AppTheme.borderColor}`,
  },
  modeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 22,
    minHeight: 36,
    flexWrap: 'wrap',
  },
  modeLabel: {
    fontSize: 14,
    fontWeight: 'normal',
    color: 'grey',
  },
  radio: {
    width: 18,
    height: 18,
    margin: 0,
    accentColor: AppTheme.primaryColor,
    cursor: 'pointer',
  },
  modeOption: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 16,
    fontWeight: 'normal',
    color: '#222222',
    cursor: 'pointer',
  },
  fieldGrid: {
    flex: 1,
    minWidth: 0,
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
    gap: 16,
    '@media (max-width: 1024px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  fieldGridCompanies: {
    flex: 1,
    minWidth: 0,
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.7fr) minmax(0, 1fr)',
    gap: 16,
    '@media (max-width: 767px)': {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    minWidth: 0,
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: 'normal',
    color: 'grey',
    margin: 0,
  },
  chipBox: {
    position: 'relative',
    boxSizing: 'border-box',
    minHeight: 44,
    padding: '6px 8px',
    border: '1px solid #E5E5E5',
    borderRadius: AppTheme.borderRadius,
    backgroundColor: AppTheme.secondaryBg,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 6,
    ':focus-within': {
      borderColor: AppTheme.primaryColor,
    },
  },
  chipBoxWithCaret: {
    paddingRight: 28,
  },
  chipBoxWithClear: {
    paddingRight: 28,
  },
  chipBoxWithClearAndCaret: {
    paddingRight: 56,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    height: 26,
    maxWidth: '100%',
    padding: '0 2px 0 8px',
    borderRadius: 8,
    backgroundColor: AppTheme.secondaryColor,
    color: '#222222',
    fontSize: 13,
    fontWeight: 'normal',
    lineHeight: '26px',
    boxSizing: 'border-box',
  },
  chipLabel: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chipRemove: {
    border: 'none',
    background: 'transparent',
    color: '#222222',
    padding: 0,
    width: 18,
    height: 18,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  chipRemoveIcon: {
    fontSize: 16,
    lineHeight: 1,
  },
  chipInput: {
    flex: 1,
    minWidth: 70,
    border: 'none',
    outline: 'none',
    height: 30,
    padding: '0 4px',
    background: 'transparent',
    font: 'inherit',
    fontSize: 16,
    color: '#222222',
    '::placeholder': {
      color: 'grey',
    },
  },
  fieldClear: {
    position: 'absolute',
    right: 4,
    top: '50%',
    marginTop: -14,
    width: 28,
    height: 28,
    padding: 0,
    border: 'none',
    background: 'transparent',
    color: 'grey',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  fieldClearWithCaret: {
    right: 32,
  },
  fieldClearIcon: {
    fontSize: 18,
  },
  caret: {
    position: 'absolute',
    right: 4,
    top: '50%',
    marginTop: -14,
    width: 28,
    height: 28,
    padding: 0,
    border: 'none',
    background: 'transparent',
    color: 'grey',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  caretIcon: {
    fontSize: 20,
  },
  linkedinInput: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    padding: '18px 28px',
    overflowY: 'auto',
  },
  linkedinLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  csvInfoIcon: {
    fontSize: 14,
    color: '#7d7d7d',
    cursor: 'pointer',
    marginLeft: 3,
  },
  urlField: {
    flex: 1,
    minHeight: 160,
    display: 'flex',
  },
  urlTextarea: {
    flex: 1,
    width: '100%',
    boxSizing: 'border-box',
    resize: 'none',
    padding: '10px 12px',
    border: '1px solid #E5E5E5',
    borderRadius: AppTheme.borderRadius,
    backgroundColor: AppTheme.secondaryBg,
    outline: 'none',
    font: 'inherit',
    fontSize: 16,
    lineHeight: 1.5,
    color: '#222222',
    ':focus': {
      borderColor: AppTheme.primaryColor,
    },
    ':disabled': {
      backgroundColor: AppTheme.primaryBg,
      color: 'grey',
      pointerEvents: 'none',
    },
    '::placeholder': {
      color: 'grey',
    },
  },
  orDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    margin: '12px 0 6px',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 1,
    color: 'grey',
  },
  orLine: {
    flex: 1,
    height: 1,
    backgroundColor: AppTheme.borderColor,
  },
  csvUploader: {
    width: '100%',
    maxWidth: 450,
  },
  csvUploaderDisabled: {
    filter: 'grayscale(1)',
    opacity: 0.6,
    pointerEvents: 'none',
  },
  menu: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: '100%',
    marginTop: 4,
    backgroundColor: AppTheme.secondaryBg,
    border: `1px solid ${AppTheme.borderColor}`,
    borderRadius: 10,
    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
    boxSizing: 'border-box',
    maxHeight: 180,
    overflowY: 'auto',
    zIndex: 2,
    '::-webkit-scrollbar': {
      width: 8,
    },
    '::-webkit-scrollbar-thumb': {
      backgroundColor: '#C1C1C1',
      borderRadius: 4,
    },
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    textAlign: 'left',
    border: 'none',
    background: 'transparent',
    padding: '8px 12px',
    font: 'inherit',
    fontSize: 16,
    color: '#222222',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: AppTheme.primaryBg,
    },
  },
  menuEmpty: {
    padding: '8px 12px',
    fontSize: 16,
    color: 'grey',
  },
  searchRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: '100%',
  },
  fieldControls: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 16,
  },
  searchField: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
  },
  searchTotal: {
    paddingRight: 20,
    color: AppTheme.primaryColor,
    fontSize: 14,
    fontWeight: 'normal',
  },
  summaryActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexShrink: 0,
  },
  searchButton: {
    height: 44,
    minWidth: 0,
    padding: '0 22px',
  },
  searchButtonText: {
    fontSize: 14,
  },
  summary: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    padding: '12px 28px',
    backgroundColor: AppTheme.secondaryBg,
    borderBottom: `1px solid ${AppTheme.borderColor}`,
  },
  summaryChips: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    minWidth: 0,
  },
  summaryChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 32,
    maxWidth: 360,
    padding: '0 12px',
    borderRadius: 8,
    backgroundColor: AppTheme.secondaryColor,
    fontSize: 13,
    color: '#222222',
    lineHeight: '32px',
    boxSizing: 'border-box',
  },
  summaryChipLabel: {
    color: 'grey',
    flexShrink: 0,
  },
  summaryChipValue: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: 600,
    color: '#222222',
    fontSize: 13,
  },
  editIcon: {
    fontSize: 16,
  },
  editButton: {
    height: 40,
    padding: '0 16px',
    flexShrink: 0,
    border: '1px solid #E5E5E5',
    borderRadius: AppTheme.borderRadius,
    backgroundColor: AppTheme.secondaryBg,
    color: '#222222',
    fontSize: 16,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  table: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: '52px minmax(0, 1fr) minmax(0, 1.1fr) minmax(0, 0.9fr)',
    alignItems: 'center',
    columnGap: 16,
    height: 38,
    flexShrink: 0,
    padding: '0 28px 0 8px',
    backgroundColor: AppTheme.primaryBg,
    color: '#9B9B9B',
    fontSize: 16,
    fontWeight: 'bold',
    borderBottom: '1px solid #E5E5E5',
  },
  tableBody: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  empty: {
    flex: 1,
    minHeight: 220,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: 28,
  },
  emptyText: {
    color: '#7D7D7D',
    fontSize: 26,
    margin: 0,
    '@media (max-width: 767px)': {
      fontSize: 16,
    },
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '52px minmax(0, 1fr) minmax(0, 1.1fr) minmax(0, 0.9fr)',
    alignItems: 'center',
    columnGap: 16,
    minHeight: 54,
    padding: '6px 28px 52px 8px',
    boxSizing: 'border-box',
    borderBottom: `1px solid ${AppTheme.borderColor}`,
    backgroundColor: AppTheme.secondaryBg,
  },
  rowSelected: {
    backgroundColor: '#f5f5f5',
  },
  checkCell: {
    display: 'flex',
    justifyContent: 'center',
  },
  checkbox: {
    width: 18,
    height: 18,
    margin: 0,
    accentColor: AppTheme.primaryColor,
    cursor: 'pointer',
  },
  menuCheckbox: {
    pointerEvents: 'none',
    flexShrink: 0,
  },
  cell: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    minWidth: 0,
  },
  ignoredCell: {
    opacity: 0.6,
  },
  primaryLine: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
  },
  personName: {
    fontSize: 16,
    fontWeight: 600,
    color: '#222222',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
  companyName: {
    fontSize: 16,
    fontWeight: 600,
    color: '#222222',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
  secondaryLine: {
    fontSize: 12,
    color: 'grey',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  metaIcon: {
    fontSize: 16,
    color: 'grey',
  },
  locationLine: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    minWidth: 0,
    maxWidth: '100%',
  },
  locationText: {
    fontSize: 14,
    fontWeight: 'normal',
    color: '#222222',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
  keywordLine: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  },
  keyword: {
    height: 26,
    padding: '0 8px',
    borderRadius: 8,
    backgroundColor: AppTheme.secondaryBg,
    border: `1px solid ${AppTheme.borderColor}`,
    fontSize: 12,
    lineHeight: '24px',
    color: '#222222',
    display: 'inline-flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
  },
  keywordMore: {
    height: 26,
    padding: '0 8px',
    borderRadius: 8,
    backgroundColor: AppTheme.secondaryBg,
    border: `1px solid ${AppTheme.borderColor}`,
    fontSize: 12,
    lineHeight: '24px',
    fontWeight: 'normal',
    color: '#222222',
    display: 'inline-flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    cursor: 'default',
  },
  iconButton: {
    width: 18,
    height: 18,
    flexShrink: 0,
    borderRadius: 4,
    border: `1px solid ${AppTheme.borderColor}`,
    color: AppTheme.primaryColor,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    textDecoration: 'none',
    fontSize: 10,
    fontWeight: 700,
    backgroundColor: AppTheme.secondaryBg,
    boxSizing: 'border-box',
    overflow: 'hidden',
    lineHeight: 1,
  },
  iconButtonFill: {
    backgroundColor: AppTheme.primaryColor,
    borderColor: AppTheme.primaryColor,
    color: AppTheme.buttonTextColor,
  },
  iconButtonLinkedIn: {
    borderColor: AppTheme.primaryColor,
  },
  footer: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 16,
    padding: '14px 28px',
    borderTop: `1px solid ${AppTheme.borderColor}`,
    backgroundColor: AppTheme.secondaryBg,
  },
  footerInfo: {
    flex: '1 1 320px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minWidth: 0,
  },
  footerNote: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 14,
    color: 'grey',
    minWidth: 0,
  },
  contactActions: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 16,
  },
  showIgnored: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    margin: 0,
    fontSize: 16,
    fontWeight: 'normal',
    color: '#222222',
    cursor: 'pointer',
  },
  footerActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexShrink: 0,
    marginLeft: 'auto',
  },
  secondaryButton: {
    height: 34,
    padding: '0 14px',
    border: `1px solid ${AppTheme.borderColor}`,
    borderRadius: 8,
    backgroundColor: AppTheme.secondaryBg,
    color: '#222222',
    fontSize: 16,
    fontWeight: 500,
    fontFamily: 'inherit',
    cursor: 'pointer',
    ':disabled': {
      color: 'grey',
      backgroundColor: AppTheme.secondaryColor,
      cursor: 'default',
    },
  },
  actionError: {
    paddingRight: 20,
    color: '#f44336',
    fontSize: 14,
  },
  confirmMessage: {
    fontSize: 16,
    lineHeight: 1.4,
    marginTop: 0,
    marginBottom: 0,
  },
  fieldsError: {
    padding: '24px 28px',
    color: '#f44336',
    fontSize: 14,
  },
  plainIcon: {
    border: 'none',
    borderRadius: 0,
    padding: 0,
  },
  tooltip: {
    zIndex: 9999999999,
  },
})
